import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { MapPin, Clock, Check, Lightning, Warning, CheckCircle, Clipboard, Plus, Trophy, Target, CalendarBlank, Package, CaretRight, ArrowRight, HouseLine, Eye, X as XIcon, Gear, CloudArrowUp } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_CLR = { Assigned: 'bg-[#EDE7F6] text-[#4527A0]', 'Can Install': 'bg-[#E8EAF6] text-[#283593]', 'In Progress': 'bg-[#E8EAF6] text-[#283593]', 'Installing': 'bg-[#E3F2FD] text-[#0D47A1]', 'Pending Approval': 'bg-[#FFF8E1] text-[#F57F17]', 'Install Done': 'bg-[#FFF3E0] text-[#E65100]', Completed: 'bg-[#E8F5E9] text-[#1B5E20]', 'NOC Approved': 'bg-[#E8F5E9] text-[#1B5E20]', Cancelled: 'bg-[#FFEBEE] text-[#B71C1C]', Pending: 'bg-[#FFF3E0] text-[#E65100]' };
const IR_STATUS_CLR = { 'Pending Review': 'bg-[#FFF8E1] text-[#F57F17]', 'Approved': 'bg-[#E8EAF6] text-[#283593]', 'Can Install': 'bg-[#E8EAF6] text-[#283593]', 'Rejected': 'bg-[#FFEBEE] text-[#B71C1C]', 'Installing': 'bg-[#E3F2FD] text-[#0D47A1]', 'Install Done': 'bg-[#FFF3E0] text-[#E65100]', 'NOC Approved': 'bg-[#E8F5E9] text-[#1B5E20]', 'Active': 'bg-[#E8F5E9] text-[#006D3B]', 'Completed': 'bg-[#E8F5E9] text-[#1B5E20]' };

export default function IMFTModule() {
  const { user } = useAuth();
  const [tab, setTab] = useState('today');
  const [tasks, setTasks] = useState([]);
  const [dashStats, setDashStats] = useState({});
  const [supervisorTeamStats, setSupervisorTeamStats] = useState({ done_tickets: 0, unfinished_tickets: 0, total_tickets: 0 });
  const [allServiceTasks, setAllServiceTasks] = useState([]);
  const [dailyTarget, setDailyTarget] = useState({});
  const [monthlyBonus, setMonthlyBonus] = useState({});
  const [inventory, setInventory] = useState([]);
  const [equipUsage, setEquipUsage] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('7');
  const [materials, setMaterials] = useState([]);
  const [custVerify, setCustVerify] = useState({ phone: '', house_no: '', street: '', gps: '' });
  const [workNotes, setWorkNotes] = useState('');
  const [powerReading, setPowerReading] = useState('');
  const [boxLoss, setBoxLoss] = useState('');
  const [routerSerial, setRouterSerial] = useState('');
  const [onuSerial, setOnuSerial] = useState('');
  // ONU Bind Installation Form states
  const [onuForm, setOnuForm] = useState({
    engineer_name: '', user_id: '', customer_name: '', cus_phone: '', cus_address: '',
    brand_name: '', bandwidth: '', mbps_plan: '', odb_name: '', odb_location: '',
    cus_location: '', odb_losses: '', home_losses: '', use_port_no: '', total_port: '',
    available_port: '', fiber_meter: '', onu_sn: '', remark: '',
    router_bind_gp: false, check_with_noc: false
  });
  const [onuPhotos, setOnuPhotos] = useState({
    box_overview: null, splitter_overview: null, box_dbm: null, user_port: null,
    home_dbm: null, speed_test: null, onu_loss: null, onu_sn_photo: null,
    onu_mounting: null, contract: null, nrc: null
  });
  const [onuPhotoNotes, setOnuPhotoNotes] = useState({});
  const [onuPhotoLabels, setOnuPhotoLabels] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [onuSubmitting, setOnuSubmitting] = useState(false);
  const [sitePhotos, setSitePhotos] = useState([]);
  // Service ticket completion states
  const [svcCompleteOpen, setSvcCompleteOpen] = useState(null);
  const [svcWorkNotes, setSvcWorkNotes] = useState('');
  const [svcPowerReading, setSvcPowerReading] = useState('');
  const [svcPhotos, setSvcPhotos] = useState([]); // [{data, label, note, time}]
  const [svcEditingPhotoIdx, setSvcEditingPhotoIdx] = useState(null);
  const [svcSubmitting, setSvcSubmitting] = useState(false);
  // SOP Service fields
  const [svcFatName, setSvcFatName] = useState('');
  const [svcErrorType, setSvcErrorType] = useState('');
  const [svcUserPortNo, setSvcUserPortNo] = useState('');
  const [svcOnuSnOld, setSvcOnuSnOld] = useState('');
  const [svcOnuSnNew, setSvcOnuSnNew] = useState('');
  const [svcRemark, setSvcRemark] = useState('');
  // SOP required photos (9 slots)
  const SVC_PHOTO_SLOTS = [
    { key: 'fat_inside', label: '1. FAT inside overall photo (with user label)' },
    { key: 'opm_at_fat', label: '2. OPM test photo at FAT' },
    { key: 'fat_closed', label: '3. FAT closed photo (After)' },
    { key: 'opm_at_home', label: '4. OPM received power photo at Home' },
    { key: 'onu_received', label: '5. ONU received power photo at Home' },
    { key: 'device_info', label: '6. Device information photo (Inside ONU)' },
    { key: 'onu_sn_back', label: '7. ONU SN photo (Back) – IF ONU Error' },
    { key: 'speed_test', label: '8. Speed test photo' },
    { key: 'onu_mounting', label: '9. ONU mounting overview photo' },
  ];
  const [svcSlotPhotos, setSvcSlotPhotos] = useState({});
  const isSupervisor = user?.role?.includes('Supervisor') || user?.role === 'Admin' || user?.role === 'SuperAdmin' || user?.role === 'General Manager' || user?.department === 'NOC';
  const isNOCApprover = user?.department === 'NOC' || user?.role === 'Admin' || user?.role === 'SuperAdmin';
  // Install Request Review states
  const [irList, setIrList] = useState([]);
  const [irStats, setIrStats] = useState({});
  const [techs, setTechs] = useState([]);
  const [techActiveJobs, setTechActiveJobs] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(null);
  const [assignTo, setAssignTo] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [rejectOpen, setRejectOpen] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const today = new Date().toISOString().split('T')[0];

  const openTechActiveJobs = async (tech) => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('app_token') || localStorage.getItem('token');
    setTechActiveJobs({ tech, jobs: [], loading: true, error: '' });
    try {
      const res = await fetch(`${API}/imft/techs/${encodeURIComponent(tech.user_id)}/active-tasks`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Unable to load active jobs');
      setTechActiveJobs({ tech, jobs: Array.isArray(data.jobs) ? data.jobs : [], loading: false, error: '' });
    } catch (error) {
      setTechActiveJobs({ tech, jobs: [], loading: false, error: error.message || 'Unable to load active jobs' });
    }
  };

  const load = async () => {
    const token = localStorage.getItem('session_token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      const [tRes, dRes, supRes, dtRes, mbRes, iRes, euRes, allTasksRes] = await Promise.all([
        fetch(`${API}/imft/tasks`, { headers }),
        fetch(`${API}/imft/dashboard-stats`, { headers }),
        fetch(`${API}/imft/supervisor-team-stats`, { headers }),
        fetch(`${API}/imft/daily-target?date=${today}`, { headers }),
        fetch(`${API}/imft/monthly-bonus`, { headers }),
        fetch(`${API}/inventory`, { headers }),
        fetch(`${API}/imft/equipment-usage`, { headers }),
        fetch(`${API}/imft/tasks`, { headers }),
      ]);
      if (tRes.ok) { const _d = await tRes.json(); setTasks(Array.isArray(_d) ? _d : []); };
      if (allTasksRes && allTasksRes.ok) { const _d = await allTasksRes.json(); setAllServiceTasks(Array.isArray(_d) ? _d : []); };
      if (dRes.ok) setDashStats(await dRes.json());
      if (supRes && supRes.ok) setSupervisorTeamStats(await supRes.json());
      if (dtRes.ok) setDailyTarget(await dtRes.json());
      if (mbRes.ok) setMonthlyBonus(await mbRes.json());
      if (iRes.ok) { const _d = await iRes.json(); setInventory(Array.isArray(_d) ? _d : []); };
      if (euRes.ok) { const _d = await euRes.json(); setEquipUsage(Array.isArray(_d) ? _d : []); };
    } catch (e) { console.error(e); }
    // Load install requests for supervisor (check role at call time)
    const canReview = user?.role?.includes('Supervisor') || user?.role === 'Admin' || user?.role === 'SuperAdmin' || user?.department === 'NOC' || user?.department === 'IMFT';
    if (canReview) {
      try {
        const [irRes, irStRes, techRes] = await Promise.all([
          fetch(`${API}/install-requests`),
          fetch(`${API}/install-requests/stats`),
          fetch(`${API}/imft/techs`)
        ]);
        if (irRes.ok) { const _d = await irRes.json(); setIrList(Array.isArray(_d) ? _d : []); };
        if (irStRes.ok) setIrStats(await irStRes.json());
        if (techRes.ok) { const _d = await techRes.json(); setTechs(Array.isArray(_d) ? _d : []); };
      } catch (e) { console.error('IR load error:', e); }
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [user]);

  const approveRequest = async (rid) => {
    if (!assignTo) { toast.error('Please select a technician'); return; }
    const tech = techs.find(t => t.user_id === assignTo);
    try {
      const res = await fetch(`${API}/install-requests/${rid}/approve`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: assignTo, assigned_to_name: tech?.name || '', review_note: reviewNote })
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Approved! Task ${data.task_id} assigned to ${data.assigned_to}`);
      setReviewOpen(null); setAssignTo(''); setReviewNote(''); load();
    } catch { toast.error('Approval failed'); }
  };

  const rejectRequest = async (rid) => {
    if (!rejectReason.trim()) { toast.error('Rejection reason required'); return; }
    try {
      const res = await fetch(`${API}/install-requests/${rid}/reject`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reject_reason: rejectReason })
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success('Request rejected');
      setRejectOpen(null); setRejectReason(''); load();
    } catch { toast.error('Rejection failed'); }
  };

  const loadHistory = async (days) => {
    setHistoryFilter(days);
    const from = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
    try {
      const res = await fetch(`${API}/imft/tasks?date_from=${from}&date_to=${today}`);
      if (res.ok) { const _d = await res.json(); setTasks(Array.isArray(_d) ? _d : []); };
    } catch {}
  };

  const updateTask = async (tid, data) => {
    try {
      const res = await fetch(`${API}/imft/tasks/${tid}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || 'Failed');
      toast.success('Updated'); setActiveJob(null); setCompleteOpen(null); load();
    } catch (err) { toast.error(err.message); }
  };

  const checkIn = (task) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => updateTask(task.task_id, { status: 'In Progress', check_in_time: new Date().toISOString(), check_in_gps: `${p.coords.latitude},${p.coords.longitude}` }),
        () => updateTask(task.task_id, { status: 'In Progress', check_in_time: new Date().toISOString(), check_in_gps: 'GPS unavailable' })
      );
    } else updateTask(task.task_id, { status: 'In Progress', check_in_time: new Date().toISOString() });
  };

  const submitOnuBind = async () => {
    if (!onuForm.engineer_name || !onuForm.user_id || !onuForm.customer_name || !onuForm.onu_sn) {
      toast.error('Please fill required fields: Engineer Name, User ID, Customer Name, ONU SN');
      return;
    }
    // Check required photos
    const requiredPhotos = ['box_overview', 'splitter_overview', 'box_dbm', 'user_port', 'home_dbm', 'speed_test', 'onu_loss', 'onu_sn_photo', 'onu_mounting', 'contract', 'nrc'];
    const missingPhotos = requiredPhotos.filter(p => !onuPhotos[p]);
    if (missingPhotos.length > 0) {
      toast.error(`Missing ${missingPhotos.length} required photo(s)`);
      return;
    }
    setOnuSubmitting(true);
    try {
      const r = await fetch(`${API}/imft/onu-bind`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ ...onuForm, photos: onuPhotos, task_id: completeOpen?.task_id || null, install_request_id: completeOpen?.install_request_id || null })
      });
      if (r.ok) {
        toast.success('ONU Bind report submitted successfully!');
        // Also update task status
        if (completeOpen?.task_id) {
          await updateTask(completeOpen.task_id, { status: 'Install Done', onu_serial: onuForm.onu_sn, router_serial: onuForm.router_bind_gp ? 'GP Bound' : '' });
        }
        if (completeOpen?.install_request_id) {
          try {
            await fetch(`${API}/install-requests/${completeOpen.install_request_id}/site-complete`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ site_photos: Object.values(onuPhotos).filter(Boolean), box_loss: onuForm.odb_losses, router_serial: onuForm.router_bind_gp ? 'GP Bound' : '', onu_serial: onuForm.onu_sn, site_engineer_notes: onuForm.remark })
            });
          } catch (e) { console.error('Site complete error:', e); }
        }
        setOnuForm({ engineer_name: '', user_id: '', customer_name: '', cus_phone: '', cus_address: '', brand_name: '', bandwidth: '', mbps_plan: '', odb_name: '', odb_location: '', cus_location: '', odb_losses: '', home_losses: '', use_port_no: '', total_port: '', available_port: '', fiber_meter: '', onu_sn: '', remark: '', router_bind_gp: false, check_with_noc: false });
        setOnuPhotos({ box_overview: null, splitter_overview: null, box_dbm: null, user_port: null, home_dbm: null, speed_test: null, onu_loss: null, onu_sn_photo: null, onu_mounting: null, contract: null, nrc: null });
        setCompleteOpen(null);
        load();
      } else { const d = await r.json(); toast.error(d.detail || 'Failed to submit'); }
    } catch (e) { toast.error('Network error'); console.error(e); }
    setOnuSubmitting(false);
  };
  const uploadOnuPhoto = async (key, file) => {
    const token = localStorage.getItem('session_token');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('photo_key', key);
    fd.append('task_id', completeOpen?.task_id || 'new');
    try {
      const r = await fetch(`${API}/imft/upload-photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (r.ok) {
        const d = await r.json();
        // Use public URL for display (served via nginx, no auth needed)
        const photoUrl = d.url || d.filename || file.name;
        setOnuPhotos(prev => ({ ...prev, [key]: photoUrl }));
        toast.success(`Photo uploaded`);
      } else {
        const errText = await r.text();
        console.error('IMFT photo upload failed:', errText);
        toast.error('Upload failed');
      }
    } catch (err) {
      console.error('IMFT photo upload error:', err);
      toast.error('Upload error');
    }
  };
  const captureOnuPhoto = (key) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*'; input.capture = 'environment';
    input.onchange = (e) => { if (e.target.files[0]) uploadOnuPhoto(key, e.target.files[0]); };
    input.click();
  };
  // Service ticket photo capture (stores as data URL locally, no upload needed)
  const captureSvcPhoto = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*'; input.capture = 'environment';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSvcPhotos(prev => [...prev, { data: ev.target.result, label: '', note: '', time: new Date().toLocaleTimeString() }]);
        toast.success('Photo captured');
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };
  const submitSvcComplete = async () => {
    if (!svcCompleteOpen) return;
    if (!svcWorkNotes.trim()) { toast.error('Work Notes is required'); return; }
    if (!svcFatName.trim()) { toast.error('FAT Name is required (SOP)'); return; }
    if (!svcErrorType.trim()) { toast.error('Error Type is required (SOP)'); return; }
    if (!svcUserPortNo.trim()) { toast.error('User Port No is required (SOP)'); return; }
    // Check required photos (at least 7 required, slot 7 optional if no ONU error)
    const requiredSlots = ['fat_inside','opm_at_fat','fat_closed','opm_at_home','onu_received','device_info','speed_test','onu_mounting'];
    const missingPhotos = requiredSlots.filter(k => !svcSlotPhotos[k]);
    if (missingPhotos.length > 0) {
      const slotLabel = SVC_PHOTO_SLOTS.find(s => s.key === missingPhotos[0])?.label || missingPhotos[0];
      toast.error(`Required photo missing: ${slotLabel}`);
      return;
    }
    setSvcSubmitting(true);
    const pwr = svcPowerReading ? { receive_power: svcPowerReading, in_range: parseFloat(svcPowerReading) >= -25 && parseFloat(svcPowerReading) <= -18, signal_status: (parseFloat(svcPowerReading) >= -25 && parseFloat(svcPowerReading) <= -18) ? 'Good' : 'Out of Range' } : {};
    const slotPhotoArray = SVC_PHOTO_SLOTS.map(s => svcSlotPhotos[s.key] ? { key: s.key, label: s.label, data: svcSlotPhotos[s.key] } : null).filter(Boolean);
    await updateTask(svcCompleteOpen.task_id, {
      status: 'Pending Approval',
      work_notes: svcWorkNotes,
      power_test: pwr,
      sop_data: {
        fat_name: svcFatName,
        error_type: svcErrorType,
        user_port_no: svcUserPortNo,
        onu_sn_old: svcOnuSnOld,
        onu_sn_new: svcOnuSnNew,
        remark: svcRemark,
      },
      photos: [...svcPhotos.map(p => ({ label: p.label, note: p.note, time: p.time, data: p.data })), ...slotPhotoArray],
    });
    setSvcWorkNotes(''); setSvcPowerReading(''); setSvcPhotos([]); setSvcSlotPhotos({});
    setSvcFatName(''); setSvcErrorType(''); setSvcUserPortNo('');
    setSvcOnuSnOld(''); setSvcOnuSnNew(''); setSvcRemark('');
    setSvcCompleteOpen(null);
    setSvcSubmitting(false);
  };
  const submitForApproval = async () => {
    if (!completeOpen) return;
    if (!routerSerial.trim()) { toast.error('Router Serial Number is required'); return; }
    if (!boxLoss.trim()) { toast.error('Box Loss reading is required'); return; }
    const pwr = powerReading ? { receive_power: powerReading, in_range: parseFloat(powerReading) >= -25 && parseFloat(powerReading) <= -18, signal_status: (parseFloat(powerReading) >= -25 && parseFloat(powerReading) <= -18) ? 'Good' : 'Out of Range' } : {};
    // Update field task
    await updateTask(completeOpen.task_id, {
      status: 'Install Done',
      customer_verify: custVerify,
      materials_used: materials.filter(m => m.item_id),
      work_notes: workNotes,
      power_test: pwr,
      box_loss: boxLoss,
      router_serial: routerSerial,
      onu_serial: onuSerial,
    });
    // Also call site-complete endpoint if this is an install request task
    if (completeOpen.install_request_id) {
      try {
        await fetch(`${API}/install-requests/${completeOpen.install_request_id}/site-complete`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            site_photos: sitePhotos,
            box_loss: boxLoss,
            router_serial: routerSerial,
            onu_serial: onuSerial,
            site_engineer_notes: workNotes,
          })
        });
      } catch (e) { console.error('Site complete error:', e); }
    }
    setMaterials([]); setCustVerify({ phone: '', house_no: '', street: '', gps: '' }); setWorkNotes(''); setPowerReading('');
    setBoxLoss(''); setRouterSerial(''); setOnuSerial(''); setSitePhotos([]);
  };

  const todayTasks = tasks.filter(t => !["Completed", "Cancelled"].includes(t.status));
  const dt = dailyTarget;
  const mb = monthlyBonus;

  return (
    <div className="space-y-4" data-testid="imft-page">
      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm cursor-pointer hover:shadow-md hover:border-[var(--md-primary)] transition-all" onClick={() => setTab('today')}>
          <p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Active Jobs</p>
          <p className="text-2xl font-bold tabular-nums text-[var(--md-primary)]">{dashStats.today_total || 0}</p>
          <p className="text-[11px] text-[var(--md-on-surface-variant)]">{dashStats.today_pending || 0} pending</p>
        </div>
        <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm cursor-pointer hover:shadow-md hover:border-[#006D3B] transition-all" onClick={() => setTab('history')}>
          <p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Completed (Month)</p>
          <p className="text-2xl font-bold tabular-nums text-[#006D3B]">{dashStats.today_completed || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm cursor-pointer hover:shadow-md hover:border-[#F57F17] transition-all" onClick={() => setTab(isSupervisor ? 'approvals' : 'today')}>
          <p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Pending Approval</p>
          <p className="text-2xl font-bold tabular-nums text-[#F57F17]">{dashStats.all_pending_approval || 0}</p>
        </div>
        {/* Daily Target */}
        <div className={`rounded-xl border-2 p-4 shadow-sm ${dt.target_met ? 'border-[#006D3B] bg-[#E8F5E9]' : 'border-[var(--md-outline-variant)] bg-white'}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Daily Target</p>
            <Target size={16} className={dt.target_met ? 'text-[#006D3B]' : 'text-[var(--md-outline)]'} weight="fill" />
          </div>
          <div className="flex items-end gap-1"><p className="text-2xl font-bold tabular-nums" style={{ color: dt.target_met ? '#006D3B' : '#0052CC' }}>{dt.progress_pct || 0}%</p><p className="text-[10px] text-[var(--md-on-surface-variant)] pb-1">{dt.points || 0}/{dt.target_points || 8} pts</p></div>
          <div className="w-full bg-[var(--md-surface-2)] rounded-full h-1.5 mt-1"><div className="h-1.5 rounded-full transition-all" style={{ width: `${dt.progress_pct || 0}%`, background: dt.target_met ? '#006D3B' : '#0052CC' }} /></div>
          <p className="text-[9px] text-[var(--md-on-surface-variant)] mt-1">{dt.installations || 0} installs + {dt.services || 0} services</p>
        </div>
        {/* Monthly Bonus */}
        <div className={`rounded-xl border-2 p-4 shadow-sm ${mb.bonus_eligible ? 'border-[#F57F17] bg-[#FFF8E1]' : 'border-[var(--md-outline-variant)] bg-white'}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Monthly Bonus</p>
            <Trophy size={16} className={mb.bonus_eligible ? 'text-[#F57F17]' : 'text-[var(--md-outline)]'} weight="fill" />
          </div>
          <p className="text-2xl font-bold tabular-nums" style={{ color: mb.bonus_eligible ? '#F57F17' : '#0052CC' }}>{mb.days_target_met || 0}<span className="text-sm font-normal text-[var(--md-on-surface-variant)]">/{mb.bonus_threshold || 20} days</span></p>
          <p className="text-[9px] text-[var(--md-on-surface-variant)]">{mb.total_jobs || 0} total jobs | {mb.bonus_eligible ? 'ELIGIBLE' : `Need ${(mb.bonus_threshold || 20) - (mb.days_target_met || 0)} more days`}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[var(--md-outline-variant)] rounded-xl p-1 flex-wrap">
          <TabsTrigger value="today" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Active Jobs</TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Job History</TabsTrigger>
          <TabsTrigger value="equipment" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Equipment Usage</TabsTrigger>
          {isSupervisor && <TabsTrigger value="services" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Services ({allServiceTasks.length})</TabsTrigger>}
          {isSupervisor && <TabsTrigger value="tech-overview" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">Tech Overview</TabsTrigger>}
          {isNOCApprover && <TabsTrigger value="approvals" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">NOC Approvals ({dashStats.all_pending_approval || 0})</TabsTrigger>}
          {isSupervisor && <TabsTrigger value="install-reviews" className="rounded-lg data-[state=active]:bg-[var(--md-primary-container)]">
            <HouseLine size={14} className="mr-1 inline" />Install Reviews {irStats.pending > 0 && <span className="ml-1 bg-[#F57F17] text-white text-[9px] rounded-full px-1.5 py-0.5">{irStats.pending}</span>}
          </TabsTrigger>}
        </TabsList>

        {/* TODAY'S JOBS */}
        <TabsContent value="today" className="space-y-3 mt-4">
          {/* Target explanation */}
          <div className="bg-[var(--md-surface-1)] rounded-xl p-3 flex items-center gap-3 text-[12px]">
            <Target size={16} className="text-[var(--md-primary)] flex-shrink-0" />
            <span><span className="font-bold">Daily Target:</span> 4 Installations (2pts each) OR 8 Services (1pt each) = 8 points. Current: <span className="font-bold text-[var(--md-primary)]">{dt.points || 0} pts</span></span>
          </div>

          {todayTasks.length === 0 ? <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-10 text-center text-sm text-[var(--md-on-surface-variant)]">No jobs assigned for today</div> :
          todayTasks.map((t, idx) => (
            <div key={t.task_id} onClick={() => setActiveJob(t)} className={`bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${t.status === 'Pending Approval' ? 'border-l-4 border-l-[#F57F17]' : t.status === 'Completed' ? 'border-l-4 border-l-[#006D3B]' : ''}`} data-testid={`job-${t.task_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${t.status === 'Completed' ? 'bg-[#006D3B]' : t.status === 'In Progress' ? 'bg-[var(--md-primary)]' : t.status === 'Pending Approval' ? 'bg-[#F57F17]' : 'bg-[var(--md-outline)]'}`}>
                    {t.status === 'Completed' ? <Check size={16} weight="bold" /> : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-xs font-bold text-[var(--md-primary)] tabular-nums">{t.task_id}</span>
                      <span className={`md3-chip text-[10px] ${STATUS_CLR[t.status] || ''}`}>{t.status}</span>
                      <span className="md3-chip text-[10px] bg-[var(--md-surface-2)]">{t.task_type || 'Service'}</span>
                    </div>
                    <p className="text-sm font-medium">{t.customer_name || 'Customer'}</p>
                    {t.customer_id && <p className="text-[11px] font-bold bg-[#E3F2FD] text-[#1565C0] px-2 py-0.5 rounded font-mono border border-[#90CAF9] inline-block mt-0.5">{t.customer_id}</p>}
                    <p className="text-xs text-[var(--md-on-surface-variant)] flex items-center gap-1 mt-0.5"><MapPin size={12} />{t.township || t.address || t.location || '-'}</p>
                    {t.customer_phone && <p className="text-xs text-[var(--md-on-surface-variant)]">{t.customer_phone}</p>}
                    {(t.customer_id || t.source_ticket) && <p className="text-[10px] text-[var(--md-primary)]">PPPoE ID: {t.customer_id || t.source_ticket}</p>}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  {(t.status === 'Assigned' || t.status === 'Can Install') && <button onClick={() => checkIn(t)} className="md3-filled-btn text-[11px] py-1.5 px-3"><Clock size={12} className="mr-1 inline" />Check In</button>}
                  {(t.status === 'In Progress' || t.status === 'Installing') && (
                    t.task_type === 'ONU Bind' || t.task_type === 'Installation' ?
                    <button onClick={() => { setCompleteOpen(t); setCustVerify({ phone: t.customer_phone || '', house_no: '', street: t.address || '', gps: t.check_in_gps || '' }); }} className="md3-filled-btn text-[11px] py-1.5 px-3 bg-[#1877F2]">ONU Bind Report</button> :
                    <button onClick={() => { setSvcCompleteOpen(t); setSvcWorkNotes(''); setSvcPowerReading(''); setSvcPhotos([]); }} className="md3-filled-btn text-[11px] py-1.5 px-3 bg-[#E65100]">Submit Report</button>
                  )}
                  {(t.status === 'Pending Approval' || t.status === 'Install Done') && <span className="text-[10px] text-[#F57F17] font-medium">Awaiting NOC</span>}
                  {(t.status === 'Completed' || t.status === 'NOC Approved') && <span className="text-[10px] text-[#006D3B] font-medium flex items-center gap-1"><CheckCircle size={12} weight="fill" />Approved</span>}
                  {t.pppoe_username && <div className="mt-1 text-[10px] text-[#006D3B] font-medium bg-[#E8F5E9] px-2 py-1 rounded">PPPoE: {t.pppoe_username}</div>}
                </div>
              </div>
              {t.power_test?.receive_power && <div className={`mt-2 p-2 rounded-lg text-xs ${t.power_test.in_range ? 'bg-[#E8F5E9]' : 'bg-[#FFEBEE]'}`}>Power: <span className="font-bold tabular-nums">{t.power_test.receive_power}dBm</span> - {t.power_test.signal_status}</div>}
            </div>
          ))}
        </TabsContent>

        {/* JOB HISTORY */}
        <TabsContent value="history" className="space-y-3 mt-4">
          <div className="flex gap-2">
            {[{ label: 'Today', val: '0' }, { label: 'Last 7 Days', val: '7' }, { label: 'Last 30 Days', val: '30' }].map(f => (
              <button key={f.val} onClick={() => f.val === '0' ? load() : loadHistory(parseInt(f.val))} className={`md3-chip text-[11px] cursor-pointer ${historyFilter === f.val ? 'bg-[var(--md-primary)] text-white' : 'bg-[var(--md-surface-2)]'}`}>{f.label}</button>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] overflow-hidden shadow-sm">
            <table className="w-full">
              <thead><tr className="bg-[var(--md-surface-1)]">
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Date</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Task</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Customer</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden md:table-cell">Type</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Status</th>
              </tr></thead>
              <tbody>
                {tasks.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-sm text-[var(--md-on-surface-variant)]">No jobs</td></tr> :
                tasks.map(t => (
                  <tr key={t.task_id} className="border-t border-[var(--md-outline-variant)] md3-table-row">
                    <td className="px-4 py-2.5 text-xs tabular-nums">{t.date}</td>
                    <td className="px-4 py-2.5 text-xs font-medium text-[var(--md-primary)]">{t.task_id}</td>
                    <td className="px-4 py-2.5 text-sm">{t.customer_name || '-'}</td>
                    <td className="px-4 py-2.5 text-xs hidden md:table-cell">{t.task_type}</td>
                    <td className="px-4 py-2.5"><span className={`md3-chip text-[10px] ${STATUS_CLR[t.status] || ''}`}>{t.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* EQUIPMENT USAGE */}
        <TabsContent value="equipment" className="mt-4">
          <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] overflow-hidden shadow-sm">
            <table className="w-full">
              <thead><tr className="bg-[var(--md-surface-1)]">
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Item</th>
                <th className="text-right text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Qty</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Task</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden md:table-cell">Customer</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden md:table-cell">Technician</th>
                <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden lg:table-cell">Date</th>
              </tr></thead>
              <tbody>
                {equipUsage.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-sm text-[var(--md-on-surface-variant)]">No equipment usage recorded</td></tr> :
                equipUsage.map((e, i) => (
                  <tr key={i} className="border-t border-[var(--md-outline-variant)] md3-table-row">
                    <td className="px-4 py-2.5"><p className="text-sm font-medium">{e.item_name}</p><p className="text-[10px] text-[var(--md-on-surface-variant)]">{e.item_id}</p></td>
                    <td className="px-4 py-2.5 text-sm text-right tabular-nums font-bold">{e.quantity}</td>
                    <td className="px-4 py-2.5 text-xs text-[var(--md-primary)]">{e.task_id}</td>
                    <td className="px-4 py-2.5 text-xs hidden md:table-cell">{e.customer_name}</td>
                    <td className="px-4 py-2.5 text-xs hidden md:table-cell">{e.technician}</td>
                    <td className="px-4 py-2.5 text-xs tabular-nums hidden lg:table-cell">{e.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* SERVICES TAB (Supervisor only) */}
        {isSupervisor && (
          <TabsContent value="services" className="space-y-3 mt-4">
            <div className="bg-[var(--md-surface-1)] rounded-xl p-3 flex items-center gap-3 text-[12px]">
              <Lightning size={16} className="text-[var(--md-primary)] flex-shrink-0" />
              <span><span className="font-bold">Service Jobs:</span> Maintenance and service tasks assigned to IMFT team.</span>
            </div>
            {allServiceTasks.length === 0 ?
              <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-10 text-center text-sm text-[var(--md-on-surface-variant)]">No service jobs found</div> :
              allServiceTasks.map(t => (
                <div key={t.task_id} onClick={() => setActiveJob(t)} className={`bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${t.status === 'Pending Approval' ? 'border-l-4 border-l-[#F57F17]' : t.status === 'Completed' ? 'border-l-4 border-l-[#006D3B]' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${t.status === 'Completed' ? 'bg-[#006D3B]' : t.status === 'In Progress' ? 'bg-[var(--md-primary)]' : t.status === 'Pending Approval' ? 'bg-[#F57F17]' : 'bg-[var(--md-outline)]'}`}>
                        {(t.assigned_name || 'T').charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-xs font-bold text-[var(--md-primary)] tabular-nums">{t.task_id}</span>
                          <span className={`md3-chip text-[10px] ${STATUS_CLR[t.status] || ''}`}>{t.status}</span>
                          <span className="md3-chip text-[10px] bg-[var(--md-surface-2)]">{t.task_type || 'Service'}</span>
                        </div>
                        <p className="text-sm font-medium">{t.customer_name || 'Customer'}</p>
                        <p className="text-xs text-[var(--md-on-surface-variant)] flex items-center gap-1"><MapPin size={12} />{t.township || t.address || t.location || '-'}</p>
                        <p className="text-xs text-[var(--md-on-surface-variant)]">Assigned: {t.assigned_to || '-'} &middot; {t.date}</p>
                        {(t.customer_id || t.source_ticket) && <p className="text-[10px] text-[var(--md-primary)]">PPPoE ID: {t.customer_id || t.source_ticket}</p>}
                      </div>
                    </div>
                    <span className={`md3-chip text-[10px] ${STATUS_CLR[t.status] || ''}`}>{t.status}</span>
                  </div>
                </div>
              ))
            }
          </TabsContent>
        )}
        {/* TECH OVERVIEW (Supervisor only) */}
        {isSupervisor && (
          <TabsContent value="tech-overview" className="space-y-3 mt-4">
            <div className="bg-[var(--md-surface-1)] rounded-xl p-3 flex items-center gap-3 text-[12px]">
              <Clipboard size={16} className="text-[var(--md-primary)] flex-shrink-0" />
              <span><span className="font-bold">Technician Overview:</span> Today's assignment counts and active task loads per technician.</span>
            </div>
            {techs.length === 0 ? (
              <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-10 text-center text-sm text-[var(--md-on-surface-variant)]">No technicians found</div>
            ) : (
              <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] overflow-hidden shadow-sm">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[var(--md-surface-1)]">
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Technician</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Active Jobs</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Active (Total)</th>
                      <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {techs.map(tech => {
                      const todayDone = tasks.filter(t => t.assigned_to === tech.user_id && t.date === today && t.status === 'Completed').length;
                      const todayPending = tasks.filter(t => t.assigned_to === tech.user_id && t.date === today && t.status !== 'Completed' && t.status !== 'Cancelled').length;
                      return (
                        <tr key={tech.user_id} className="border-t border-[var(--md-outline-variant)] hover:bg-[var(--md-surface-1)]">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-[var(--md-primary-container)] flex items-center justify-center text-xs font-bold text-[var(--md-primary)]">{(tech.name || 'T').charAt(0)}</div>
                              <div>
                                <p className="text-sm font-medium">{tech.name}</p>
                                <p className="text-[10px] text-[var(--md-on-surface-variant)]">{tech.user_id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-lg font-bold tabular-nums text-[var(--md-primary)]">{tech.today_tasks || 0}</span>
                            <p className="text-[10px] text-[var(--md-on-surface-variant)]">{todayDone} done / {todayPending} pending</p>
                          </td>
                          <td className="px-4 py-3">
                            <button type="button" onClick={() => openTechActiveJobs(tech)} className="group text-left rounded-md px-1 py-0.5 hover:bg-[var(--md-primary-container)] focus:outline-none focus:ring-2 focus:ring-[var(--md-primary)]">
                              <span className="text-sm font-bold tabular-nums group-hover:text-[var(--md-primary)]">{tech.active_tasks || 0}</span>
                              <span className="text-[10px] text-[var(--md-on-surface-variant)] ml-1">active · View IDs</span>
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            {(tech.active_tasks || 0) >= 7 ? (
                              <span className="md3-chip text-[10px] bg-[#FFEBEE] text-[#B71C1C]">Overloaded</span>
                            ) : (tech.active_tasks || 0) >= 4 ? (
                              <span className="md3-chip text-[10px] bg-[#FFF8E1] text-[#F57F17]">Busy</span>
                            ) : (
                              <span className="md3-chip text-[10px] bg-[#E8F5E9] text-[#006D3B]">Available</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Dialog open={!!techActiveJobs} onOpenChange={(open) => { if (!open) setTechActiveJobs(null); }}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{techActiveJobs?.tech?.name || 'Technician'} — Active Jobs</DialogTitle></DialogHeader>
                <p className="text-xs text-[var(--md-on-surface-variant)]">These are the stored jobs included in this technician’s Active (Total) count.</p>
                {!techActiveJobs ? null : techActiveJobs.loading ? <div className="py-10 text-center text-sm text-[var(--md-on-surface-variant)]">Loading active jobs…</div> : techActiveJobs.error ? <div className="rounded-lg bg-[#FFEBEE] p-3 text-sm text-[#B71C1C]">{techActiveJobs.error}</div> : techActiveJobs.jobs.length === 0 ? <div className="py-10 text-center text-sm text-[var(--md-on-surface-variant)]">No active jobs found for this technician.</div> : <div className="space-y-2">{techActiveJobs.jobs.map(job => <button type="button" key={job.task_id} onClick={() => { setTechActiveJobs(null); setActiveJob(job); }} className="w-full rounded-xl border border-[var(--md-outline-variant)] p-3 text-left hover:bg-[var(--md-surface-1)]"><div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs font-bold text-[var(--md-primary)]">{job.task_id}</span><span className={`md3-chip text-[10px] ${STATUS_CLR[job.status] || ''}`}>{job.status || '—'}</span><span className="md3-chip text-[10px] bg-[var(--md-surface-2)]">{job.task_type || 'Service'}</span></div><p className="mt-1 text-sm font-medium">{job.customer_name || '—'}</p><p className="mt-0.5 text-xs text-[var(--md-on-surface-variant)]">{job.customer_phone || '—'} · {job.township || job.address || '—'} · Assigned: {job.date || job.created_at || '—'}</p></button>)}</div>}
              </DialogContent>
            </Dialog>
            {/* All tasks breakdown by technician */}
            <h3 className="text-sm font-semibold mt-4">Today's Task Breakdown</h3>
            <div className="space-y-2">
              {techs.map(tech => {
                const techTasks = tasks.filter(t => t.assigned_to === tech.user_id && t.date === today);
                if (techTasks.length === 0) return null;
                return (
                  <div key={tech.user_id} className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-3 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-[var(--md-primary-container)] flex items-center justify-center text-[10px] font-bold text-[var(--md-primary)]">{(tech.name || 'T').charAt(0)}</div>
                      <span className="text-sm font-semibold">{tech.name}</span>
                      <span className="ml-auto text-[11px] text-[var(--md-on-surface-variant)]">{techTasks.length} job{techTasks.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-1">
                      {techTasks.map(t => (
                        <div key={t.task_id} onClick={() => setActiveJob(t)} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--md-surface-1)] cursor-pointer hover:bg-[var(--md-surface-2)]">
                          <span className="text-[10px] font-bold text-[var(--md-primary)] tabular-nums w-24 flex-shrink-0">{t.task_id}</span>
                          <span className="text-[11px] flex-1 truncate">{t.customer_name || '-'}</span>
                          <span className="md3-chip text-[9px] flex-shrink-0">{t.task_type || 'Service'}</span>
                          <span className={`md3-chip text-[9px] flex-shrink-0 ${STATUS_CLR[t.status] || ''}`}>{t.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        )}
        {/* NOC APPROVALS (NOC only) */}
        {isNOCApprover && (
          <TabsContent value="approvals" className="space-y-3 mt-4">
            {tasks.filter(t => t.status === 'Pending Approval').length === 0 ? <div className="bg-white rounded-xl border p-10 text-center text-sm text-[var(--md-on-surface-variant)]">All jobs approved</div> :
            tasks.filter(t => t.status === 'Pending Approval').map(t => (
              <div key={t.task_id} className="bg-white rounded-xl border-2 border-[#F57F17] p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1"><span className="text-xs font-bold text-[var(--md-primary)]">{t.task_id}</span><span className="md3-chip text-[10px] bg-[var(--md-surface-2)]">{t.task_type}</span></div>
                    <p className="text-sm font-medium">{t.customer_name}</p>
                    <p className="text-xs text-[var(--md-on-surface-variant)]">{t.assigned_name} &middot; {t.date}</p>
                  </div>
                </div>
                {t.work_notes && <div className="p-3 bg-[var(--md-surface-1)] rounded-lg text-xs mb-2"><p className="font-medium mb-0.5">Work Notes:</p><p>{t.work_notes}</p></div>}
                {t.customer_verify && <div className="p-3 bg-[var(--md-surface-1)] rounded-lg text-xs mb-2"><p className="font-medium mb-0.5">Customer Verified:</p><p>Phone: {t.customer_verify.phone} | House: {t.customer_verify.house_no} | {t.customer_verify.street}</p></div>}
                {t.power_test?.receive_power && <div className={`p-2 rounded-lg text-xs mb-2 ${t.power_test.in_range ? 'bg-[#E8F5E9]' : 'bg-[#FFEBEE]'}`}>Power: <span className="font-bold">{t.power_test.receive_power}dBm</span> - {t.power_test.signal_status}</div>}
                {t.materials_used?.length > 0 && <div className="p-3 bg-[var(--md-surface-1)] rounded-lg text-xs mb-2"><p className="font-medium mb-1">Equipment Used:</p>{t.materials_used.map((m, i) => <span key={i} className="md3-chip text-[10px] bg-[var(--md-primary-container)] mr-1">{m.item_name || m.item_id}: {m.quantity}</span>)}</div>}
                <div className="flex gap-2 mt-2">
                  <button onClick={() => updateTask(t.task_id, { status: 'Completed', approved_by: user?.name || 'NOC' })} className="md3-filled-btn flex-1 text-xs bg-[#006D3B]" data-testid={`approve-${t.task_id}`}><CheckCircle size={14} className="mr-1 inline" />Approve & Complete</button>
                  <button onClick={() => updateTask(t.task_id, { status: 'Assigned', approved_by: '' })} className="md3-outlined-btn flex-1 text-xs text-[var(--md-error)]">Reject</button>
                </div>
              </div>
            ))}
          </TabsContent>
        )}
        {/* INSTALL REQUEST REVIEWS (Supervisor only) */}
        {isSupervisor && (
          <TabsContent value="install-reviews" className="space-y-4 mt-4">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm"><p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Total</p><p className="text-2xl font-bold tabular-nums text-[var(--md-primary)]">{irStats.total || 0}</p></div>
              <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm"><p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Pending Review</p><p className="text-2xl font-bold tabular-nums text-[#F57F17]">{irStats.pending || 0}</p></div>
              <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm"><p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Can Install</p><p className="text-2xl font-bold tabular-nums text-[#283593]">{irStats.can_install || irStats.approved || 0}</p></div>
              <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm"><p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Install Done</p><p className="text-2xl font-bold tabular-nums text-[#E65100]">{irStats.install_done || 0}</p></div>
              <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm"><p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">NOC Approved</p><p className="text-2xl font-bold tabular-nums text-[#1B5E20]">{irStats.noc_approved || irStats.completed || 0}</p></div>
              <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] p-4 shadow-sm"><p className="text-[11px] uppercase text-[var(--md-on-surface-variant)]">Rejected</p><p className="text-2xl font-bold tabular-nums text-[#B71C1C]">{irStats.rejected || 0}</p></div>
            </div>

            {/* Pending Reviews */}
            <h3 className="text-sm font-medium">Pending Install Requests</h3>
            {irList.filter(ir => ir.status === 'Pending Review').length === 0 ?
              <div className="bg-white rounded-xl border p-10 text-center text-sm text-[var(--md-on-surface-variant)]">No pending install requests</div> :
              irList.filter(ir => ir.status === 'Pending Review').map(ir => (
                <div key={ir.request_id} className="bg-white rounded-xl border-2 border-[#F57F17] p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-bold text-[var(--md-primary)] tabular-nums">{ir.request_id}</span>
                        <span className={`md3-chip text-[10px] ${IR_STATUS_CLR[ir.status] || ''}`}>{ir.status}</span>
                        {ir.brand && <span className="md3-chip text-[10px] bg-[var(--md-surface-2)]">{ir.brand}</span>}
                      </div>
                      <p className="text-sm font-medium">{ir.customer_name}</p>
                      <p className="text-xs text-[var(--md-on-surface-variant)]">{ir.customer_phone} | {ir.township} | {ir.address}</p>
                      <p className="text-xs text-[var(--md-on-surface-variant)]">{ir.package} - {ir.bandwidth}</p>
                      {ir.remark && <p className="text-xs text-[var(--md-on-surface-variant)] mt-1">Remark: {ir.remark}</p>}
                      <p className="text-[10px] text-[var(--md-on-surface-variant)] mt-1">By: {ir.submitted_by_name} | {new Date(ir.submitted_at).toLocaleString()}</p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <button onClick={() => { setReviewOpen(ir); setAssignTo(''); setReviewNote(''); }} className="md3-filled-btn text-[11px] py-1.5 px-3 bg-[#006D3B]"><Check size={12} className="mr-1 inline" />Approve</button>
                      <button onClick={() => { setRejectOpen(ir); setRejectReason(''); }} className="md3-outlined-btn text-[11px] py-1.5 px-3 text-[var(--md-error)]"><XIcon size={12} className="mr-1 inline" />Reject</button>
                    </div>
                  </div>
                </div>
              ))
            }

            {/* All Requests History */}
            <h3 className="text-sm font-medium mt-4">All Install Requests</h3>
            <div className="bg-white rounded-xl border border-[var(--md-outline-variant)] overflow-hidden shadow-sm">
              <table className="w-full">
                <thead><tr className="bg-[var(--md-surface-1)]">
                  <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">ID</th>
                  <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Customer</th>
                  <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden md:table-cell">Township</th>
                  <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden md:table-cell">Package</th>
                  <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3">Status</th>
                  <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden lg:table-cell">Assigned</th>
                  <th className="text-left text-[11px] font-medium text-[var(--md-on-surface-variant)] px-4 py-3 hidden lg:table-cell">Submitted</th>
                </tr></thead>
                <tbody>
                  {irList.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-sm text-[var(--md-on-surface-variant)]">No install requests</td></tr> :
                  irList.map(ir => (
                    <tr key={ir.request_id} className="border-t border-[var(--md-outline-variant)] md3-table-row">
                      <td className="px-4 py-2.5 text-xs font-medium text-[var(--md-primary)]">{ir.request_id}</td>
                      <td className="px-4 py-2.5"><p className="text-sm">{ir.customer_name}</p><p className="text-[10px] text-[var(--md-on-surface-variant)]">{ir.customer_phone}</p></td>
                      <td className="px-4 py-2.5 text-xs hidden md:table-cell">{ir.township}</td>
                      <td className="px-4 py-2.5 text-xs hidden md:table-cell">{ir.package} {ir.bandwidth}</td>
                      <td className="px-4 py-2.5"><span className={`md3-chip text-[10px] ${IR_STATUS_CLR[ir.status] || ''}`}>{ir.status}</span></td>
                      <td className="px-4 py-2.5 text-xs hidden lg:table-cell">{ir.assigned_to_name || '-'}</td>
                      <td className="px-4 py-2.5 text-xs tabular-nums hidden lg:table-cell">{new Date(ir.submitted_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

      </Tabs>

      {/* Approve Install Request Dialog */}
      <Dialog open={!!reviewOpen} onOpenChange={() => setReviewOpen(null)}>
        <DialogContent className="sm:max-w-md bg-white rounded-2xl">
          {reviewOpen && <>
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#006D3B]"><CheckCircle size={20} weight="fill" />Approve Install Request</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="p-3 bg-[var(--md-surface-1)] rounded-xl text-sm">
                <p className="font-medium">{reviewOpen.customer_name}</p>
                <p className="text-xs text-[var(--md-on-surface-variant)]">{reviewOpen.customer_phone} | {reviewOpen.township}</p>
                <p className="text-xs text-[var(--md-on-surface-variant)]">{reviewOpen.package} - {reviewOpen.bandwidth}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-[var(--md-on-surface-variant)] mb-1 block">Assign to Technician *</label>
                <Select value={assignTo} onValueChange={setAssignTo}>
                  <SelectTrigger className="bg-[var(--md-surface-1)] border-[var(--md-outline-variant)] rounded-xl h-10"><SelectValue placeholder="Select technician" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {techs.map(t => (
                      <SelectItem key={t.user_id} value={t.user_id}>{t.name} ({t.today_tasks || 0} jobs today, {t.active_tasks || 0} active)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><label className="text-xs font-medium text-[var(--md-on-surface-variant)] mb-1 block">Review Note</label><textarea value={reviewNote} onChange={e => setReviewNote(e.target.value)} className="md3-input min-h-[50px] resize-none text-sm" placeholder="Optional note..." /></div>
              <div className="flex gap-2">
                <button onClick={() => setReviewOpen(null)} className="md3-outlined-btn flex-1">Cancel</button>
                <button onClick={() => approveRequest(reviewOpen.request_id)} className="md3-filled-btn flex-1 bg-[#006D3B]"><Check size={14} className="mr-1 inline" />Approve & Assign</button>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>

      {/* Reject Install Request Dialog */}
      <Dialog open={!!rejectOpen} onOpenChange={() => setRejectOpen(null)}>
        <DialogContent className="sm:max-w-md bg-white rounded-2xl">
          {rejectOpen && <>
            <DialogHeader><DialogTitle className="text-[var(--md-error)]">Reject Install Request - {rejectOpen.request_id}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="p-3 bg-[var(--md-surface-1)] rounded-xl text-sm">
                <p className="font-medium">{rejectOpen.customer_name}</p>
                <p className="text-xs text-[var(--md-on-surface-variant)]">{rejectOpen.customer_phone} | {rejectOpen.township}</p>
              </div>
              <div><label className="text-xs font-medium mb-1 block">Rejection Reason *</label><textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} required className="md3-input min-h-[60px] resize-none" placeholder="Explain why installation is not feasible..." /></div>
              <div className="p-3 bg-[#FFEBEE] rounded-xl text-xs flex items-center gap-2">
                <Warning size={14} className="text-[var(--md-error)] flex-shrink-0" />
                <span>The FTTH Sale staff will be notified that this installation request has been rejected.</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setRejectOpen(null)} className="md3-outlined-btn flex-1">Back</button>
                <button onClick={() => rejectRequest(rejectOpen.request_id)} className="md3-filled-btn flex-1 bg-[var(--md-error)]"><XIcon size={14} className="mr-1 inline" />Reject Request</button>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog open={!!activeJob} onOpenChange={v => !v && setActiveJob(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-[var(--md-primary)] tabular-nums">{activeJob?.task_id}</span>
              <span className={`md3-chip text-[10px] ${STATUS_CLR[activeJob?.status] || ''}`}>{activeJob?.status}</span>
            </DialogTitle>
          </DialogHeader>
          {activeJob && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[var(--md-surface-1)] rounded-xl p-3">
                  <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Customer</p>
                  {(activeJob.customer_id || activeJob.pppoe_username) ? (
                    <p className="text-[12px] font-bold font-mono bg-[#E3F2FD] text-[#1565C0] px-2 py-1 rounded border border-[#90CAF9] inline-block mt-0.5 break-all">{activeJob.customer_id || activeJob.pppoe_username}</p>
                  ) : (
                    <p className="text-sm font-semibold">{activeJob.customer_name || '-'}</p>
                  )}
                  {activeJob.customer_phone && <p className="text-xs text-[var(--md-on-surface-variant)] mt-1">{activeJob.customer_phone}</p>}
                </div>
                <div className="bg-[var(--md-surface-1)] rounded-xl p-3">
                  <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Assigned To</p>
                  <p className="text-sm font-semibold">{activeJob.assigned_name || '-'}</p>
                  <p className="text-[10px] text-[var(--md-on-surface-variant)]">{activeJob.date}</p>
                </div>
              </div>
              <div className="bg-[var(--md-surface-1)] rounded-xl p-3">
                <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Location</p>
                <p className="text-sm">{activeJob.address || activeJob.township || activeJob.location || '-'}</p>
                {activeJob.township && activeJob.address && <p className="text-xs text-[var(--md-on-surface-variant)]">{activeJob.township}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[var(--md-surface-1)] rounded-xl p-3">
                  <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Task Type</p>
                  <p className="text-sm font-semibold">{activeJob.task_type || '-'}</p>
                </div>
                <div className="bg-[var(--md-surface-1)] rounded-xl p-3">
                  <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Priority</p>
                  <p className="text-sm font-semibold">{activeJob.priority || 'Normal'}</p>
                </div>
              </div>
              {activeJob.description && (
                <div className="bg-[var(--md-surface-1)] rounded-xl p-3">
                  <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Description</p>
                  <p className="text-sm">{activeJob.description}</p>
                </div>
              )}
              {(activeJob.customer_id || activeJob.source_ticket) && (
                <div className="bg-[#E8EAF6] rounded-xl p-3">
                  <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">PPPoE ID</p>
                  <p className="text-sm font-semibold text-[var(--md-primary)]">{activeJob.customer_id || activeJob.source_ticket}</p>
                </div>
              )}
              {activeJob.work_notes && (
                <div className="bg-[var(--md-surface-1)] rounded-xl p-3">
                  <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Work Notes</p>
                  <p className="text-sm">{activeJob.work_notes}</p>
                </div>
              )}
              {activeJob.power_test?.receive_power && (
                <div className={`p-3 rounded-xl text-sm ${activeJob.power_test.in_range ? 'bg-[#E8F5E9]' : 'bg-[#FFEBEE]'}`}>
                  <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Power Test</p>
                  <p><span className="font-bold tabular-nums">{activeJob.power_test.receive_power}dBm</span> — {activeJob.power_test.signal_status}</p>
                </div>
              )}
              {activeJob.check_in_time && (
                <div className="bg-[var(--md-surface-1)] rounded-xl p-3">
                  <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Check-in Time</p>
                  <p className="text-sm">{new Date(activeJob.check_in_time).toLocaleString()}</p>
                  {activeJob.check_in_gps && <p className="text-[10px] text-[var(--md-on-surface-variant)]">GPS: {activeJob.check_in_gps}</p>}
                </div>
              )}
              {isSupervisor && activeJob.status === 'Pending Approval' && (
                <div className="flex gap-2 pt-2">
                  <button onClick={() => { updateTask(activeJob.task_id, { status: 'Completed', approved_by: user?.name || 'NOC' }); setActiveJob(null); }} className="md3-filled-btn flex-1 text-xs bg-[#006D3B]"><CheckCircle size={14} className="mr-1 inline" />Approve & Complete</button>
                  <button onClick={() => { updateTask(activeJob.task_id, { status: 'Assigned', approved_by: '' }); setActiveJob(null); }} className="md3-outlined-btn flex-1 text-xs text-[var(--md-error)]">Reject</button>
                </div>
              )}
              <button onClick={() => setActiveJob(null)} className="md3-outlined-btn w-full text-sm mt-2">Close</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Service Ticket Completion Dialog */}
      <Dialog open={!!svcCompleteOpen} onOpenChange={() => setSvcCompleteOpen(null)}>
        <DialogContent className="sm:max-w-lg bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
          {svcCompleteOpen && <>
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#E65100]"><Clipboard size={20} weight="fill" />Service Completion Report ({svcCompleteOpen.task_id})</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-3">
              {/* Customer info */}
              <div className="bg-[var(--md-surface-1)] rounded-xl p-3">
                <p className="text-[10px] text-[var(--md-on-surface-variant)] uppercase mb-1">Customer</p>
                <p className="text-sm font-semibold">{svcCompleteOpen.customer_name}</p>
                {svcCompleteOpen.customer_id && <span className="text-[11px] font-bold bg-[#E3F2FD] text-[#1565C0] px-2 py-0.5 rounded font-mono border border-[#90CAF9]">{svcCompleteOpen.customer_id}</span>}
                {svcCompleteOpen.customer_phone && <p className="text-xs text-[var(--md-on-surface-variant)] mt-1">{svcCompleteOpen.customer_phone}</p>}
              </div>
              {/* SOP Service Done Format */}
              <div className="border border-[#E65100] rounded-xl p-3 bg-[#FFF3E0]">
                <p className="text-[11px] font-bold text-[#E65100] mb-3 uppercase tracking-wide">📋 SOP — Service Done Format (Required)</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">4. FAT Name *</label>
                    <input value={svcFatName} onChange={e => setSvcFatName(e.target.value)} className="md3-input text-sm" placeholder="e.g. FAT-001" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">5. Error Type *</label>
                    <input value={svcErrorType} onChange={e => setSvcErrorType(e.target.value)} className="md3-input text-sm" placeholder="e.g. Red Light, ONU Error" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">6. User Port No *</label>
                    <input value={svcUserPortNo} onChange={e => setSvcUserPortNo(e.target.value)} className="md3-input text-sm" placeholder="e.g. Port 3" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">7. ONU SN (Old) — if ONU Error</label>
                    <input value={svcOnuSnOld} onChange={e => setSvcOnuSnOld(e.target.value)} className="md3-input text-sm" placeholder="Old ONU serial" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">8. ONU SN (New) — if ONU Error</label>
                    <input value={svcOnuSnNew} onChange={e => setSvcOnuSnNew(e.target.value)} className="md3-input text-sm" placeholder="New ONU serial" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">9. Remark</label>
                    <input value={svcRemark} onChange={e => setSvcRemark(e.target.value)} className="md3-input text-sm" placeholder="Additional notes" />
                  </div>
                </div>
              </div>
              {/* Work Notes */}
              <div>
                <label className="text-xs font-medium text-[var(--md-on-surface-variant)] mb-1 block">Work Notes *</label>
                <textarea value={svcWorkNotes} onChange={e => setSvcWorkNotes(e.target.value)} className="md3-input min-h-[80px] resize-none text-sm" placeholder="Describe work performed..." />
              </div>
              {/* Power Test */}
              <div>
                <label className="text-xs font-medium text-[var(--md-on-surface-variant)] mb-1 block">Power Test (dBm) — Standard: -18 to -25 dBm</label>
                <input type="number" step="0.1" value={svcPowerReading} onChange={e => setSvcPowerReading(e.target.value)} className="md3-input text-sm" placeholder="e.g. -22.5" />
                {svcPowerReading && <p className={`text-[11px] mt-1 font-medium ${parseFloat(svcPowerReading) >= -25 && parseFloat(svcPowerReading) <= -18 ? 'text-[#006D3B]' : 'text-[#BA1A1A]'}`}>{parseFloat(svcPowerReading) >= -25 && parseFloat(svcPowerReading) <= -18 ? '✓ Good Signal' : '⚠ Out of Range'}</p>}
              </div>
              {/* SOP Required Photos */}
              <div className="border border-[#E65100] rounded-xl p-3">
                <h4 className="text-[13px] font-semibold text-[#E65100] mb-2">📷 SOP Required Photos (All photos must include lat/lon, address, time & date)</h4>
                <div className="space-y-2">
                  {SVC_PHOTO_SLOTS.map(slot => {
                    const isRequired = slot.key !== 'onu_sn_back';
                    const photo = svcSlotPhotos[slot.key];
                    return (
                      <div key={slot.key} className={`border rounded-lg p-2 ${photo ? 'border-[#C8E6C9] bg-[#F1F8E9]' : isRequired ? 'border-[#FFCDD2] bg-[#FFF8F8]' : 'border-[var(--md-outline-variant)] bg-white'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-medium flex-1">{slot.label} {isRequired && <span className="text-red-500">*</span>}</span>
                          {photo ? (
                            <div className="flex items-center gap-1">
                              <img src={photo} alt={slot.label} className="w-10 h-10 object-cover rounded" />
                              <button onClick={() => setSvcSlotPhotos(prev => ({...prev, [slot.key]: null}))} className="text-[9px] text-red-500 bg-[#FFEBEE] rounded px-1.5 py-0.5">✕</button>
                            </div>
                          ) : (
                            <label className="md3-outlined-btn text-[10px] py-0.5 px-2 cursor-pointer border-[#E65100] text-[#E65100] flex-shrink-0">
                              📷 Upload
                              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => {
                                const file = e.target.files[0]; if (!file) return;
                                const reader = new FileReader();
                                reader.onload = (ev) => { setSvcSlotPhotos(prev => ({...prev, [slot.key]: ev.target.result})); toast.success(`Photo uploaded: ${slot.label.split('.')[0]}`); };
                                reader.readAsDataURL(file);
                              }} />
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[var(--md-on-surface-variant)] mt-2">* Required | Photo 7 (ONU SN Back) only required if ONU Error</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSvcCompleteOpen(null)} className="md3-outlined-btn flex-1">Cancel</button>
                <button onClick={submitSvcComplete} disabled={svcSubmitting} className="md3-filled-btn flex-1 bg-[#E65100]">{svcSubmitting ? 'Submitting...' : 'Submit for Approval'}</button>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>
      {/* ONU Bind Installation Report Dialog */}
      <Dialog open={!!completeOpen} onOpenChange={() => setCompleteOpen(null)}>
        <DialogContent className="sm:max-w-2xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto">
          {completeOpen && <>
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#1877F2]"><Clipboard size={20} weight="fill" />ONU Bind - Installation Report ({completeOpen.task_id})</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-3">
              {/* Router Bind GP & Check with NOC */}
              <div className="flex gap-4 items-center p-3 bg-[#E3F2FD] rounded-xl">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={onuForm.router_bind_gp} onChange={e => setOnuForm({...onuForm, router_bind_gp: e.target.checked})} className="w-4 h-4 accent-[#1877F2]" />
                  <span className="text-sm font-medium text-[#1877F2]">Router Bind GP</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={onuForm.check_with_noc} onChange={e => setOnuForm({...onuForm, check_with_noc: e.target.checked})} className="w-4 h-4 accent-[#1877F2]" />
                  <span className="text-sm font-medium text-[#1877F2]">Check with NOC</span>
                </label>
              </div>
              {/* ONU Bind Format Fields */}
              <div className="border border-[var(--md-outline-variant)] rounded-xl p-4">
                <h4 className="text-[13px] font-semibold mb-3 text-[#1877F2]">ONU Bind Format</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">1. Engineer Name *</label><input value={onuForm.engineer_name} onChange={e => setOnuForm({...onuForm, engineer_name: e.target.value})} className="md3-input text-sm" placeholder="Engineer name" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">2. User ID *</label><input value={onuForm.user_id} onChange={e => setOnuForm({...onuForm, user_id: e.target.value})} className="md3-input text-sm" placeholder="User ID" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">3. Customer Name *</label><input value={onuForm.customer_name} onChange={e => setOnuForm({...onuForm, customer_name: e.target.value})} className="md3-input text-sm" placeholder="Customer name" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">4. Cus Phone</label><input value={onuForm.cus_phone} onChange={e => setOnuForm({...onuForm, cus_phone: e.target.value})} className="md3-input text-sm" placeholder="Phone number" /></div>
                  <div className="col-span-2"><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">5. Cus Address</label><input value={onuForm.cus_address} onChange={e => setOnuForm({...onuForm, cus_address: e.target.value})} className="md3-input text-sm" placeholder="Full address" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">6. Brand Name</label><input value={onuForm.brand_name} onChange={e => setOnuForm({...onuForm, brand_name: e.target.value})} className="md3-input text-sm" placeholder="Brand" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">7. Bandwidth</label><input value={onuForm.bandwidth} onChange={e => setOnuForm({...onuForm, bandwidth: e.target.value})} className="md3-input text-sm" placeholder="e.g. 30 Mbps" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">8. Mbps Plan</label><input value={onuForm.mbps_plan} onChange={e => setOnuForm({...onuForm, mbps_plan: e.target.value})} className="md3-input text-sm" placeholder="Plan name" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">9. ODB Name</label><input value={onuForm.odb_name} onChange={e => setOnuForm({...onuForm, odb_name: e.target.value})} className="md3-input text-sm" placeholder="ODB name" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">10. ODB Location</label><input value={onuForm.odb_location} onChange={e => setOnuForm({...onuForm, odb_location: e.target.value})} className="md3-input text-sm" placeholder="GPS or address" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">11. CUS Location</label><input value={onuForm.cus_location} onChange={e => setOnuForm({...onuForm, cus_location: e.target.value})} className="md3-input text-sm" placeholder="GPS or address" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">12. ODB Losses (dB)</label><input type="number" step="0.01" value={onuForm.odb_losses} onChange={e => setOnuForm({...onuForm, odb_losses: e.target.value})} className="md3-input text-sm" placeholder="e.g. 0.35" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">13. Home Losses (dB)</label><input type="number" step="0.01" value={onuForm.home_losses} onChange={e => setOnuForm({...onuForm, home_losses: e.target.value})} className="md3-input text-sm" placeholder="e.g. 0.25" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">14. Use Port No</label><input value={onuForm.use_port_no} onChange={e => setOnuForm({...onuForm, use_port_no: e.target.value})} className="md3-input text-sm" placeholder="Port number" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">15. Total Port</label><input value={onuForm.total_port} onChange={e => setOnuForm({...onuForm, total_port: e.target.value})} className="md3-input text-sm" placeholder="Total ports" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">16. Available Port</label><input value={onuForm.available_port} onChange={e => setOnuForm({...onuForm, available_port: e.target.value})} className="md3-input text-sm" placeholder="Available" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">17. Fiber Meter</label><input value={onuForm.fiber_meter} onChange={e => setOnuForm({...onuForm, fiber_meter: e.target.value})} className="md3-input text-sm" placeholder="Meters used" /></div>
                  <div><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">18. ONU SN *</label><input value={onuForm.onu_sn} onChange={e => setOnuForm({...onuForm, onu_sn: e.target.value})} className="md3-input text-sm" placeholder="ONU Serial Number" /></div>
                  <div className="col-span-2"><label className="text-[11px] font-medium text-[var(--md-on-surface-variant)] mb-1 block">19. Remark</label><textarea value={onuForm.remark} onChange={e => setOnuForm({...onuForm, remark: e.target.value})} className="md3-input text-sm min-h-[50px] resize-none" placeholder="Additional notes..." /></div>
                </div>
              </div>
              {/* Required Photos */}
              <div className="border border-[var(--md-outline-variant)] rounded-xl p-4">
                <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2 text-[#1877F2]"><CloudArrowUp size={16} />Required Photos (11)</h4>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'box_overview', label: '1. Box overview photo' },
                    { key: 'splitter_overview', label: '2. Splitter overview photo' },
                    { key: 'box_dbm', label: '3. Box dBm photo' },
                    { key: 'user_port', label: '4. User port photo' },
                    { key: 'home_dbm', label: '5. Home dBm photo' },
                    { key: 'speed_test', label: '6. Speed test photo' },
                    { key: 'onu_loss', label: '7. ONU loss photo' },
                    { key: 'onu_sn_photo', label: '8. ONU SN photo' },
                    { key: 'onu_mounting', label: '9. ONU mounting overview' },
                    { key: 'contract', label: '10. Contract' },
                    { key: 'nrc', label: '11. NRC' },
                  ].map(({ key, label }) => (
                    <div key={key} className={`p-2 rounded-lg border ${onuPhotos[key] ? 'border-[#006D3B] bg-[#E8F5E9]' : 'border-[var(--md-outline-variant)] bg-[var(--md-surface-1)]'}`}>
                      <p className="text-[10px] font-medium mb-1 truncate">{label}</p>
                      {onuPhotos[key] ? (
                        <div className="space-y-1">
                          <img src={onuPhotos[key]} alt={label} className="w-full h-20 object-cover rounded-lg" />
                          {/* Custom label */}
                          <input
                            type="text"
                            value={onuPhotoLabels[key] || ''}
                            onChange={e => setOnuPhotoLabels(prev => ({...prev, [key]: e.target.value}))}
                            placeholder="Photo label..."
                            className="w-full text-[9px] px-1.5 py-1 border border-[#C8E6C9] rounded bg-white"
                          />
                          {/* Notes */}
                          {editingNote === key ? (
                            <div className="space-y-1">
                              <textarea
                                value={onuPhotoNotes[key] || ''}
                                onChange={e => setOnuPhotoNotes(prev => ({...prev, [key]: e.target.value}))}
                                placeholder="Add note..."
                                className="w-full text-[9px] px-1.5 py-1 border border-[#C8E6C9] rounded bg-white resize-none min-h-[40px]"
                              />
                              <button onClick={() => setEditingNote(null)} className="text-[9px] text-[#006D3B] font-medium">Done</button>
                            </div>
                          ) : (
                            <button onClick={() => setEditingNote(key)} className="text-[9px] text-[#1877F2] underline">{onuPhotoNotes[key] ? '✎ ' + onuPhotoNotes[key].slice(0,20) + (onuPhotoNotes[key].length > 20 ? '...' : '') : '+ Add note'}</button>
                          )}
                          <div className="flex gap-1">
                            <button onClick={() => captureOnuPhoto(key)} className="flex-1 text-[9px] bg-[#E3F2FD] text-[#1877F2] rounded py-0.5">Retake</button>
                            <button onClick={() => { setOnuPhotos(prev => ({...prev, [key]: null})); setOnuPhotoNotes(prev => ({...prev, [key]: ''})); setOnuPhotoLabels(prev => ({...prev, [key]: ''})); }} className="text-[9px] text-red-500 bg-[#FFEBEE] rounded px-1.5 py-0.5">✕</button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <button onClick={() => captureOnuPhoto(key)} className="w-full bg-[#E3F2FD] text-[#1877F2] rounded-lg py-2 text-[10px] font-bold active:bg-[#BBDEFB] flex items-center justify-center gap-1">
                            📷 Capture Photo
                          </button>
                          <label className="block">
                            <span className="text-[9px] text-[var(--md-on-surface-variant)] block text-center">or choose file</span>
                            <input type="file" accept="image/*" onChange={e => { if (e.target.files[0]) uploadOnuPhoto(key, e.target.files[0]); }} className="text-[9px] w-full mt-0.5" />
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-[var(--md-on-surface-variant)] mt-2">{Object.values(onuPhotos).filter(Boolean).length}/11 photos uploaded</p>
              </div>
              {/* Workflow notice */}
              <div className="p-3 bg-[#E3F2FD] rounded-xl text-[12px] flex items-center gap-2">
                <Warning size={16} className="text-[#1877F2] flex-shrink-0" />
                <span>Report will be sent to <span className="font-bold">NOC Center</span> for verification and approval.</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setCompleteOpen(null)} className="md3-outlined-btn flex-1">Cancel</button>
                <button onClick={submitOnuBind} disabled={onuSubmitting} className="md3-filled-btn flex-1 bg-[#1877F2]" data-testid="submit-report-btn">{onuSubmitting ? 'Submitting...' : 'Submit ONU Bind Report'}</button>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
